import { useEffect, useMemo, useState } from 'react'
import { useWireValue } from '@forminator/react-wire'
import cn from 'clsx'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom'

import * as store from '@store'
import * as actions from '@actions'
import CustomModal from '@/components/shared/CustomModal/CustomModal'
import { ModalBody, ModalFooter, ModalHeader } from '../shared/CustomModal'
import TractJoinList from './TractJoinList'
import { MAIN_TAB, TAB_TYPE_DEED } from '@/constants'
import ConfirmationModal from '../shared/ConfirmationModal'

export const AssignTracts = ({
    showAdd,
    showHideAdd,
    showCreateTract,
    runsheetId,
    isRunsheetTab,
    tractId,
    tabId,
    ownershipType,
    setOwnershipType,
}) => {
    
    const runsheetTabs = useWireValue(store.runsheetTabs)
    const [selectedTractId, setSelectedTractId] = useState(tractId)
    const [overrideConfirmationModal, setOverrideConfirmationModal] = useState(false)
    const selectedTab = runsheetTabs.find(rt => rt.id === tabId)
    
    const tracts = useWireValue(store.tracts)
    
    const sortedTracts = useMemo(() => {
        // sorting first if the tract id is found
        // then sorting to group all of the same runsheet
        // then sorting by name if none of the others apply
        return tracts.sort((a, b) => {
            if (a.id === tractId) return -1
            if (b.id === tractId) return 1
            
            if (a.joins.some(j => j.runsheetId === runsheetId)) return -1
            if (b.joins.some(j => j.runsheetId === runsheetId)) return 1
            return a.name.localeCompare(b.name)
        })
    }, [tracts])
    const unassign = tractId && selectedTractId && selectedTractId === tractId
    
    const assignTract = async () => {
        const savingTractId = selectedTractId || tractId
        const runsheetTabId = tabId !== MAIN_TAB ? tabId : null
        const tract = tracts.find(t => t.id === savingTractId)
        
        const assignText = unassign ? 'Unassigned' : 'Assigned'
        const assignDirection = unassign ? 'from' : 'to'
        const assignTarget = isRunsheetTab ? 'Tab' : 'Runsheet'
        
        try {
            
            if (savingTractId) {
                await actions.assignTractToRunsheet(savingTractId, {
                    runsheetId,
                    runsheetTabId,
                    ownershipType,
                    unassign,
                })
                
                toast.success(`${assignText} Tract "${tract.name.trim()}" ${assignDirection} ${assignTarget}`)
            }
            
            await showHideAdd(!showAdd)
        } catch (err) {
            toast.error(`Failed to ${assignText} tract ${assignDirection} ${assignTarget}`)
        }
        
    }
    
    const handleAssignTract = async () => {
        const runsheetTabId = tabId !== MAIN_TAB ? tabId : null
        
        if (selectedTractId && ownershipType === TAB_TYPE_DEED) {
            const tract = tracts.find(t => t.id === selectedTractId)
            
            const notifyUser =tract.joins?.some(j => (j.runsheetId !== runsheetId || j.runsheetTabId !== runsheetTabId)
                && j.ownershipType === ownershipType)
            
            if (notifyUser) {
                return setOverrideConfirmationModal(true)
            }
        }
        
        assignTract()
        
    }
    
    useEffect(() => {
        actions.fetchTracts()
    }, [])
    
    return (
        <CustomModal className="max-w-[1200px] w-[1200px]"
            open={showAdd} modalHandler={() => showHideAdd(!showAdd)} center>
            <ModalHeader>Assign Tract{isRunsheetTab && ` to tab "${selectedTab.name}"`}</ModalHeader>
            <ModalBody>
                <div className="flex gap-2 items-center mb-4">
                    
                    <label>Assign as:</label>
                    
                    <div className="join">
                        <button
                            onClick={() => !isRunsheetTab && setOwnershipType('deed')}
                            className={cn('btn btn-xs join-item', {
                                'btn-primary': ownershipType === 'deed',
                                'bg-base-100': ownershipType === 'lease',
                                'cursor-not-allowed': isRunsheetTab,
                            })}>
                                Deed
                        </button>
                        <button
                            onClick={() => !isRunsheetTab && setOwnershipType('lease')}
                            className={cn('btn border btn-xs join-item', {
                                'btn-primary': ownershipType === 'lease',
                                'bg-base-100': ownershipType === 'deed',
                                'cursor-not-allowed': isRunsheetTab,
                            })}>
                                Lease
                        </button>
                    </div>
                </div>
                <div className="max-h-[500px] overflow-auto bg-base-100">
                    
                    <table className="table table-sm table-sticky-headers table-zebra">
                        <thead>
                            <tr>
                                <th className="!bg-base-100 w-8"></th>
                                <th className="!bg-base-100 max-w-[320px] w-[320px]">Tract</th>
                                <th className="!bg-base-100">Acreage</th>
                                <th className="!bg-base-100">Runsheets</th>
                                <th className="!bg-base-100">County</th>
                                <th className="!bg-base-100"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {(sortedTracts || [])
                                .map(tract => (
                                    <tr key={tract.id}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                disabled={runsheetTabs.some(rt => rt.tractId === tract.id
                                                    && rt.id !== tabId)}
                                                className="radio radio-primary"
                                                value={tract.id}
                                                id={tract.id}
                                                name="tract"
                                                checked={selectedTractId === tract.id}
                                                onChange={evt =>
                                                    setSelectedTractId(evt.target.checked ? tract.id : null)} />
                                        </td>
                                        <td>
                                            <label className="text-left line-clamp-1 cursor-pointer" htmlFor={tract.id}>
                                                {tract.name}
                                            </label>
                                        </td>
                                        <td>{tract.grossAcreage}</td>
                                        <td>
                                            <TractJoinList tract={tract} />
                                        </td>
                                        <td><div>{tract.county?.name}</div>
                                            <div className="text-slate-400 text-sm -mt-1">
                                                {tract.county?.state?.name}
                                            </div>
                                        </td>
                                        <td>
                                            <Link
                                                className="btn btn-secondary btn-xs"
                                                to={`/tract/${tract.id}/ownership`}>
                                                See Ownership
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
                
                <ConfirmationModal 
                    showModal={overrideConfirmationModal}
                    showHideModal={() => setOverrideConfirmationModal(false)}
                    title="Override existing assignment"
                    message="Are you sure you want to override the existing assignment of this tract?"
                    onConfirm={() => assignTract()} />
            </ModalBody>
            <ModalFooter>
                <button className="btn btn-secondary" onClick={() => {
                    showHideAdd(!showAdd)
                    showCreateTract(true)
                }}>New Tract</button>
                <button
                    type="button"
                    className="btn btn-primary btn-outline"
                    onClick={() => {
                        showHideAdd(!showAdd)
                    }}>
                    Cancel
                </button>
                <button
                    type="submit"
                    className="mr-3 btn btn-primary"
                    onClick={handleAssignTract}>
                    {unassign ? 'Unassign' : 'Assign'}
                </button>
            </ModalFooter>
        </CustomModal>
    )
}
